// Phase 7 client substitution domain (blueprint §15.8, checkpoints 7.0/7B).
// Pure rules: which slot an item answers, whether it may be replaced, what
// the replacement looks like. Ids, time and connectivity are inputs, never
// read here. Effective profiles stay origin-owned (effectiveExecutionProfile).
import { createClientUuid } from "./clientIds";
import { personalNoteFor } from "./exerciseNotes";

// A Weekly Plan slot without an exercise (§7.2) is held as this local-only
// item until an exercise is chosen. It is never synced, never logged and has
// no effective profile; the server never sees the origin.
export const UNMAPPED_PLACEHOLDER_ORIGIN = "UNMAPPED_PLACEHOLDER";

export const SUBSTITUTION_ERRORS = Object.freeze({
  NOT_ACTIVE: "NOT_ACTIVE",
  OFFLINE: "OFFLINE",
  NOT_A_SLOT: "NOT_A_SLOT",
  SLOT_PERFORMED: "SLOT_PERFORMED",
  CARDIO_BOUNDARY: "CARDIO_BOUNDARY",
  INVALID_TARGET: "INVALID_TARGET",
  NO_ORIGINAL: "NO_ORIGINAL",
  CONFIRM_DISCARD_REQUIRED: "CONFIRM_DISCARD_REQUIRED",
  INVALID_IDS: "INVALID_IDS",
});

export class SubstitutionError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "SubstitutionError";
    this.code = code;
  }
}

function fail(code, message) {
  throw new SubstitutionError(code, message);
}

// The snapshot slot (block + exercise) an item answers, or null.
export function snapshotSlotForItem(record, item) {
  if (item?.prescribedExerciseKey == null) return null;
  for (const block of record?.server?.snapshot?.blocks || []) {
    for (const exercise of block.exercises || []) {
      if (exercise.exerciseKey === item.prescribedExerciseKey) return { block, exercise };
    }
  }
  return null;
}

export function isMappedSlot(slot) {
  return Boolean(slot?.exercise?.exerciseId);
}

// Structural Cardio class of a prescription slot (mirrors backend
// substitutionContract.slotStructuralClass). Snapshot-owned; never derived
// from the item's effective profile.
export function slotStructuralClass(slot) {
  const measurementType = slot?.exercise?.profile?.measurementType;
  if (measurementType) return measurementType === "CARDIO_MINUTES" ? "CARDIO" : "NON_CARDIO";
  return slot?.exercise?.cardio != null || slot?.block?.grouping === "CARDIO"
    ? "CARDIO"
    : "NON_CARDIO";
}

function measurementClass(measurementType) {
  return measurementType === "CARDIO_MINUTES" ? "CARDIO" : "NON_CARDIO";
}

// The explicit placeholder, or a legacy local record whose unmapped slot was
// stored as a PRESCRIBED item without an exercise (pre-7C clients).
export function isUnmappedPlaceholder(record, item) {
  if (item?.origin === UNMAPPED_PLACEHOLDER_ORIGIN) return true;
  if (item?.exerciseId) return false;
  const slot = snapshotSlotForItem(record, item);
  return Boolean(slot) && !isMappedSlot(slot);
}

export function createUnmappedPlaceholder({ block, exercise, position, itemId }) {
  return {
    itemId,
    position,
    blockKey: block.blockKey,
    laneIndex: exercise.laneIndex ?? null,
    origin: UNMAPPED_PLACEHOLDER_ORIGIN,
    prescribedExerciseKey: exercise.exerciseKey,
    substitutedFromExerciseId: null,
    exerciseId: null,
    exerciseName: exercise.exerciseName || null,
    measurementType: null,
    loadMode: null,
    suggestedFamily: null,
    executionProfile: null,
    notes: null,
    skipped: false,
    cardio: null,
    sets: [],
  };
}

// Evidence that the slot was ever performed (§15.8): a first-performance
// instant on any retained set, or a Cardio completion. Current values alone
// never decide it, so clearing a value never restores eligibility.
export function slotEverPerformed(item) {
  return (item?.sets || []).some((set) => set?.performedAt != null) ||
    item?.cardio?.completedAt != null ||
    item?.cardio?.completed === true;
}

// True when replacing the item would discard entered, unperformed input.
// Structural empty rows are not draft facts.
export function substitutionWouldDiscardDraftFacts(item) {
  return (item?.sets || []).some((set) =>
    set?.load != null || set?.reps != null || set?.seconds != null || set?.rir != null
  ) || item?.cardio?.minutes != null;
}

function usableProfile(profile) {
  return Boolean(profile?.measurementType && profile?.loadMode);
}

// The rows a replacement answers: the current item's rows (prescribed rows
// and ADDED extras keep their structure), or for a placeholder the snapshot
// slot's prescribed rows. A Cardio slot has none.
function replacementRowsTemplate(record, item, slot) {
  if (slotStructuralClass(slot) === "CARDIO") return [];
  if (isUnmappedPlaceholder(record, item)) {
    return (slot.exercise.sets || []).map((set) => ({
      setIndex: set.setIndex,
      origin: "PRESCRIBED",
      prescribedSetKey: set.setKey,
      setType: set.setType || "WORKING",
    }));
  }
  return (item.sets || []).map((set) => ({
    setIndex: set.setIndex,
    origin: set.origin,
    prescribedSetKey: set.prescribedSetKey ?? null,
    setType: set.setType,
  }));
}

export function substitutionSetCount(record, item) {
  const slot = snapshotSlotForItem(record, item);
  return slot ? replacementRowsTemplate(record, item, slot).length : 0;
}

// Picker query for this slot (§15.8, 7B): rank against the ORIGINAL
// prescribed exercise, filter by the snapshot slot class. An unmapped slot
// sends only its class and gets the ordinary name order.
export function substitutePickerQuery(record, item, { showAll = false } = {}) {
  const slot = snapshotSlotForItem(record, item);
  if (!slot) fail(SUBSTITUTION_ERRORS.NOT_A_SLOT, "Only a prescribed slot can be substituted.");
  return {
    ...(isMappedSlot(slot) ? { substituteFor: slot.exercise.exerciseId } : {}),
    slotClass: slotStructuralClass(slot),
    showAll: showAll ? "true" : "false",
  };
}

// The server never lists the original; only the client knows the exercise the
// slot currently holds, so it removes it without reordering the rest.
export function filterSubstituteCandidates(record, item, candidates = []) {
  if (isUnmappedPlaceholder(record, item) || !item?.exerciseId) return candidates;
  return candidates.filter((candidate) => candidate.exerciseId !== item.exerciseId);
}

// The slot's planned exercise as an ordinary picker candidate, while the slot
// holds a replacement: Substitute is the one way back to it. Built from the
// snapshot, so it carries the planned profile and the planned conflict. Null
// when the slot already holds its planned exercise or never had one.
export function plannedSubstituteCandidate(record, item) {
  const slot = snapshotSlotForItem(record, item);
  if (!isMappedSlot(slot) || isUnmappedPlaceholder(record, item)) return null;
  if (item.exerciseId === slot.exercise.exerciseId) return null;
  return {
    exerciseId: slot.exercise.exerciseId,
    name: slot.exercise.exerciseName || null,
    equipmentCategory: null,
    bodyParts: [],
    executionProfile: slot.exercise.profile || null,
    conflict: slot.exercise.constraintConflict || null,
  };
}

// Choosing the slot's planned exercise is a return to the prescription, not a
// substitution: the item becomes PRESCRIBED again. The athlete never sees the
// distinction; every caller resolves its target here.
export function resolveSubstitutionTarget(record, item, target) {
  if (!target || target.restore) return target;
  const slot = snapshotSlotForItem(record, item);
  return isMappedSlot(slot) && target.exerciseId === slot.exercise.exerciseId
    ? { restore: true }
    : target;
}

// Checks every client-side rule that does not depend on connectivity or the
// discard confirmation. Returns { noop: true } when the target is the exercise
// the slot already holds.
export function assertSubstitutionAllowed(record, item, requested) {
  const target = resolveSubstitutionTarget(record, item, requested);
  if ((record?.document?.status || record?.status) !== "ACTIVE") {
    fail(SUBSTITUTION_ERRORS.NOT_ACTIVE, "Exercises can be substituted only during an active workout.");
  }
  const slot = snapshotSlotForItem(record, item);
  if (!item || !slot) fail(SUBSTITUTION_ERRORS.NOT_A_SLOT, "Only a prescribed slot can be substituted.");
  const placeholder = isUnmappedPlaceholder(record, item);
  if (target.restore) {
    if (!isMappedSlot(slot)) fail(SUBSTITUTION_ERRORS.NO_ORIGINAL, "This slot has no original exercise.");
    if (!placeholder && item.exerciseId === slot.exercise.exerciseId) return { noop: true, slot };
  } else {
    if (!target.exerciseId) fail(SUBSTITUTION_ERRORS.INVALID_TARGET, "Choose an exercise.");
    if (!placeholder && target.exerciseId === item.exerciseId) return { noop: true, slot };
    if (!usableProfile(target.executionProfile)) {
      fail(SUBSTITUTION_ERRORS.INVALID_TARGET, "The chosen exercise has no execution profile.");
    }
    if (measurementClass(target.executionProfile.measurementType) !== slotStructuralClass(slot)) {
      fail(SUBSTITUTION_ERRORS.CARDIO_BOUNDARY, "Cardio and strength exercises cannot replace each other.");
    }
  }
  if (!placeholder && slotEverPerformed(item)) {
    fail(SUBSTITUTION_ERRORS.SLOT_PERFORMED, "A performed exercise cannot be substituted.");
  }
  return { noop: false, slot };
}

// The NEW item that answers the same slot (§15.8): new itemId and setIds, the
// same slot identity and row structure, no carried execution facts, skipped
// cleared. The note is the NEW exercise's own carried or drafted note, never
// the replaced exercise's. A restore returns to the snapshot exercise.
export function buildSubstitutionReplacement(record, item, requested, { itemId, setIds, at }) {
  const target = resolveSubstitutionTarget(record, item, requested);
  const slot = snapshotSlotForItem(record, item);
  const rows = replacementRowsTemplate(record, item, slot);
  const previousIds = new Set((item.sets || []).map((set) => set.setId));
  if (
    !itemId || itemId === item.itemId ||
    !Array.isArray(setIds) || setIds.length !== rows.length ||
    new Set(setIds).size !== setIds.length ||
    setIds.some((setId) => !setId || previousIds.has(setId))
  ) {
    fail(SUBSTITUTION_ERRORS.INVALID_IDS, "A substitution needs a new item id and new set ids.");
  }
  const restore = Boolean(target.restore);
  const profile = restore ? slot.exercise.profile : target.executionProfile;
  const cardio = slotStructuralClass(slot) === "CARDIO";
  return {
    itemId,
    position: item.position,
    blockKey: item.blockKey,
    laneIndex: item.laneIndex ?? null,
    origin: restore ? "PRESCRIBED" : "SUBSTITUTED",
    prescribedExerciseKey: item.prescribedExerciseKey,
    substitutedFromExerciseId: restore ? null : slot.exercise.exerciseId || null,
    exerciseId: restore ? slot.exercise.exerciseId : target.exerciseId,
    exerciseName: restore ? slot.exercise.exerciseName : target.name || target.exerciseName || null,
    measurementType: profile.measurementType,
    loadMode: profile.loadMode,
    suggestedFamily: profile.suggestedFamily || null,
    // SUBSTITUTED: the picker's provisional profile until the server freezes
    // its own. PRESCRIBED: a convenience copy; the snapshot stays the owner.
    executionProfile: profile,
    notes: personalNoteFor(
      record,
      restore ? slot.exercise.exerciseId : target.exerciseId,
      restore ? null : target.personalNote
    ),
    skipped: false,
    cardio: cardio ? { minutes: null, completed: false, completedAt: null } : null,
    sets: rows.map((row, index) => ({
      setId: setIds[index],
      setIndex: row.setIndex,
      origin: row.origin,
      prescribedSetKey: row.prescribedSetKey,
      setType: row.setType,
      load: null,
      reps: null,
      seconds: null,
      rir: null,
      performedAt: null,
      lastEditedAt: at,
      enteredAfterFinish: false,
    })),
  };
}

// The reducer action for a substitution or a restore. Ids are minted here, at
// the intent boundary, so the reducer stays deterministic.
export function createSubstitutionAction(record, itemId, requested, {
  confirmDiscard = false,
  uuidFactory = createClientUuid,
} = {}) {
  const item = (record?.document?.items || []).find((candidate) => candidate.itemId === itemId);
  const target = resolveSubstitutionTarget(record, item, requested);
  const count = item ? substitutionSetCount(record, item) : 0;
  return {
    type: "SUBSTITUTE_ITEM",
    itemId,
    target: target.restore
      ? { restore: true }
      : {
          exerciseId: target.exerciseId,
          name: target.name ?? target.exerciseName ?? null,
          executionProfile: target.executionProfile,
          personalNote: target.personalNote ?? null,
        },
    confirmDiscard: Boolean(confirmDiscard),
    ids: {
      itemId: uuidFactory(),
      setIds: Array.from({ length: count }, () => uuidFactory()),
    },
  };
}

// ---------------------------------------------------------------------------
// Presentation (7D). Derived from the same rules; components never re-decide
// eligibility, they only choose what to show.

// What the execution card may offer for this item. `kind` is "placeholder"
// (an unmapped slot waiting for an exercise), "slot" (an item answering a
// prescribed slot) or null (ADDED / Free items: no substitution).
export function substitutionPresentation(record, item, { online = false } = {}) {
  const slot = snapshotSlotForItem(record, item);
  if (!slot) return { kind: null, available: false, offline: false };
  const placeholder = isUnmappedPlaceholder(record, item);
  const active = (record?.document?.status || record?.status) === "ACTIVE";
  const eligible = active && (placeholder || !slotEverPerformed(item));
  const substituted = !placeholder && item.origin === "SUBSTITUTED";
  return {
    kind: placeholder ? "placeholder" : "slot",
    // Structurally possible now (ACTIVE and never performed); connectivity is
    // reported separately so the UI can explain it.
    available: eligible,
    // A planned exercise keeps its Substitute action for the whole ACTIVE
    // workout. Once the slot was performed the action stays in place, locked:
    // the rule itself is unchanged.
    locked: active && !eligible,
    offline: eligible && online !== true,
    substituted,
    originalName: isMappedSlot(slot) ? slot.exercise.exerciseName || null : null,
    slotClass: slotStructuralClass(slot),
  };
}

// Athlete wording for the two kinds of conflict; the stored reason codes are
// never shown.
const AVOIDED_TAG = "Marked to avoid";
const MOVEMENT_LIMIT_TAG = "Movement limit";
const CONFLICT_REASON_LABELS = Object.freeze({
  blocked_exercise_id: AVOIDED_TAG,
  blocked_joint_stress_tag: MOVEMENT_LIMIT_TAG,
  blocked_movement_pattern: MOVEMENT_LIMIT_TAG,
});

// Compact picker tags from structured reasons, deterministic and deduplicated
// ("Marked to avoid" before "Movement limit").
export function conflictTagLabels(conflict) {
  const labels = new Set((conflict?.reasons || [])
    .map((reason) => CONFLICT_REASON_LABELS[reason])
    .filter(Boolean));
  return [AVOIDED_TAG, MOVEMENT_LIMIT_TAG].filter((label) => labels.has(label));
}

// The canonical one-line warning for a prescribed conflict (§15.8), built from
// the snapshot's reasons, never from its stored summary text.
export function prescribedConflictCopy(conflict) {
  const labels = conflictTagLabels(conflict);
  if (labels.includes(AVOIDED_TAG)) return "You marked this exercise to avoid";
  if (labels.includes(MOVEMENT_LIMIT_TAG)) return "This exercise may conflict with your current movement limits";
  return null;
}

// Athlete copy for a refused substitution; internal codes are never shown.
export function substitutionErrorCopy(error) {
  switch (error?.code) {
    case SUBSTITUTION_ERRORS.OFFLINE:
      return "Needs a connection";
    case SUBSTITUTION_ERRORS.SLOT_PERFORMED:
      return "This exercise already has logged work, so it can’t be substituted.";
    case SUBSTITUTION_ERRORS.NOT_ACTIVE:
      return "Exercises can be substituted only during an active workout.";
    case SUBSTITUTION_ERRORS.CARDIO_BOUNDARY:
      return "Cardio and strength exercises can’t replace each other.";
    default:
      return "That exercise couldn’t be substituted. Try again.";
  }
}
